// ============================================================
// Firestore Collections Schema
// ============================================================
//
// /users/{uid}
//   role: "admin" | "promoter"
//   name: string
//   email: string
//   shopName: string          (promoter only)
//   baseSalary: number        (promoter only)
//   createdAt: timestamp
//
// /targets/{targetId}
//   promoterId: string (uid)
//   month: string  "2025-05"
//   sellTarget: number
//   highendTarget: number
//   createdAt: timestamp
//
// /sales/{saleId}
//   promoterId: string
//   promoterName: string
//   model: string
//   serial: string
//   isHighend: boolean
//   saleDate: string  "2025-05-13"
//   status: "pending" | "approved" | "rejected"
//   month: string  "2025-05"
//   submittedAt: timestamp
//   approvedAt: timestamp | null
//
// /attendance/{attendanceId}
//   promoterId: string
//   date: string  "2025-05-13"
//   checkinTime: string  "08:02"
//   checkinPhoto: string  (Storage URL)
//   checkinLat: number
//   checkinLng: number
//   checkoutTime: string | null
//   checkoutPhoto: string | null
//   checkoutLat: number | null
//   checkoutLng: number | null
//   totalHours: number | null
//   isHoliday: boolean
//   holidayMultiplier: number  (default 1, ngày lễ = 3)
//
// /holidays/{holidayId}
//   name: string
//   date: string  "2025-05-01"
//   multiplier: number  (3 = ngày lễ luật VN)
//
// /skus/{skuId}
//   model: string
//   brand: string
//   cpu: string
//   gpu: string
//   ram: string
//   ssd: string
//   lcd: string
//   battery: string
//   ports: string
//   upgradeable: string
//   isHighend: boolean
//   createdAt: timestamp
//
// /bonusPrograms/{programId}
//   name: string
//   startDate: string
//   endDate: string
//   skus: string[]  (model names)
//   amountPerUnit: number
//   createdAt: timestamp
//
// /notifications/{notifId}
//   title: string
//   body: string
//   fileUrl: string | null
//   sentTo: "all" | string[]  (uid list)
//   createdAt: timestamp
//
// ============================================================

// /bonusConfig/main
//   salesTiers: { minRate: number, bonus: number }[]   (sắp xếp giảm dần theo minRate)
//   highendTiers: { maxTarget: number, baseBonus: number, overRate: number }[]
//   updatedAt: timestamp

export const COLLECTIONS = {
  USERS: "users",
  TARGETS: "targets",
  SALES: "sales",
  ATTENDANCE: "attendance",
  ATTENDANCE_APPEALS: "attendanceAppeals",
  HOLIDAYS: "holidays",
  SKUS: "skus",
  BONUS_PROGRAMS: "bonusPrograms",
  EVENT_BONUSES: "eventBonuses",
  INCOME_HISTORY: "incomeHistory",
  TRAINING_DOCS: "trainingDocs",
  PAYROLL_ADJUSTMENTS: "payrollAdjustments",
  PAYROLL_CLOSINGS: "payrollClosings",
  COMPANY_COST_CONFIG: "companyCostConfig",
  TAX_CONFIG: "taxConfig",
  PAYROLL_CONFIG: "payrollConfig",          // {yyyy-MM} → work, bonus, tax, rules (phiên bản theo tháng áp dụng)
  PAYROLL_COST_CONFIG: "payrollCostConfig", // {yyyy-MM} → cost (chỉ admin đọc — nhạy cảm)
  OT_REQUESTS: "otRequests",                // {promoterId}_{date} → đăng ký tăng ca
  // taxConfig/main → DEFAULT_TAX_CONFIG (giảm trừ, bậc thuế, tỉ lệ BH người lao động)
  INSURANCE_ADJUSTMENTS: "insuranceAdjustments",
  // insuranceAdjustments/{promoterId}_{month}
  //   enabled: boolean        — có thuộc diện đóng BH tháng đó không
  //   baseOverride: number|null — ghi đè mức lương đóng BH riêng cho tháng đó
  BONUS_CONFIG: "bonusConfig",
  NOTIFICATIONS: "notifications",
  SUPERVISOR_ASSIGNMENTS: "supervisorAssignments",
  // supervisorAssignments/{supervisorId}
  //   promoterIds: string[]   — danh sách uid promoter được quản lý
  AUDIT_LOGS: "auditLogs",
  SKU_CONFIG: "skuConfig",
  // skuSpecs/{partNo}   → { partNo, values: { <cột master>: giá trị }, uploadedAt } — toàn bộ thông số từ file master
  // skuMaster/current   → { fileName, sheetName, columns: [cột theo thứ tự], count, uploadedAt, uploadedBy }
  SKU_SPECS: "skuSpecs",
  SKU_MASTER: "skuMaster",
  // skuConfig/main  → { fields: FieldConfig[], updatedAt }
  // auditLogs/{logId}
  //   type: "sale_approved" | "sale_rejected" | "target_set"
  //   actorName: string       — tên người thực hiện
  //   actorRole: string       — "admin" | "supervisor"
  //   promoterId: string
  //   promoterName: string
  //   detail: string          — mô tả ngắn
  //   meta: object            — dữ liệu bổ sung (tuỳ type)
  //   createdAt: timestamp
};

// /attendanceAppeals/{appealId}
//   promoterId: string
//   promoterName: string
//   attendanceId: string | null   (null nếu không có record cho ngày đó)
//   date: string  "2025-05-13"
//   reason: string
//   status: "pending" | "approved" | "rejected"
//   submittedAt: timestamp
//   reviewedAt: timestamp | null
//   adminNote: string | null

// Giá trị mặc định — dùng khi chưa có cấu hình lưu trong Firestore
export const DEFAULT_SALES_TIERS = [
  { minRate: 1.2, bonus: 9750000 },
  { minRate: 1.0, bonus: 6500000 },
  { minRate: 0.8, bonus: 4500000 },
];

// Thưởng Highend theo TỪNG MÁY + accelerator khi vượt target cá nhân
export const DEFAULT_HIGHEND_CONFIG = { unitRate: 300000, overRate: 500000 };

// Chi phí công ty trả qua agency (admin xem) — % tính trên LƯƠNG CƠ BẢN
export const DEFAULT_COMPANY_COST = {
  siPct: 17.5,    // BHXH doanh nghiệp
  hiPct: 3,       // BHYT doanh nghiệp
  uiPct: 1,       // BH thất nghiệp doanh nghiệp
  unionPct: 2,    // Phí công đoàn
  pai: 90000,     // Bảo hiểm tai nạn (cố định/người/tháng)
  vatPct: 8,      // VAT trên (Gross + các phí + phí quản lý)
  usdRate: 26100, // tỷ giá quy đổi USD ở dòng tổng của file lương xuất ra
  // Phí quản lý dịch vụ theo số nhân viên có công trong tháng (đ/người/tháng)
  mgmtTiers: [
    { maxStaff: 1, fee: 3000000 },
    { maxStaff: 5, fee: 1500000 },
    { maxStaff: 12, fee: 1150000 },
  ],
};

// Tra phí quản lý/người theo số nhân viên (vượt bậc cuối → dùng bậc cuối)
export const getMgmtFeePerPerson = (staffCount, tiers = DEFAULT_COMPANY_COST.mgmtTiers) => {
  if (!staffCount || staffCount <= 0) return 0;
  const sorted = [...tiers].sort((a, b) => a.maxStaff - b.maxStaff);
  for (const t of sorted) {
    if (staffCount <= t.maxStaff) return Number(t.fee) || 0;
  }
  return Number(sorted[sorted.length - 1]?.fee) || 0;
};

export const calcSalesBonus = (sold, target, tiers = DEFAULT_SALES_TIERS) => {
  if (target === 0) return 0;
  const hitRate = sold / target;
  const sorted = [...tiers].sort((a, b) => b.minRate - a.minRate);
  for (const tier of sorted) {
    if (hitRate >= tier.minRate) return tier.bonus;
  }
  return 0;
};

export const calcHighendBonus = (sold, target, cfg = DEFAULT_HIGHEND_CONFIG) => {
  // Backward-compat: nếu nhận config cũ (mảng tiers) hoặc thiếu → dùng mặc định
  const c = (cfg && typeof cfg.unitRate === "number") ? cfg : DEFAULT_HIGHEND_CONFIG;
  if (!sold || sold <= 0) return 0;
  const t = target > 0 ? target : 0;
  const upTo = t > 0 ? Math.min(sold, t) : sold;   // máy tới target → unitRate
  const over = t > 0 ? Math.max(0, sold - t) : 0;  // máy vượt target → overRate
  return upTo * c.unitRate + over * c.overRate;
};

export const calcTotalSalary = ({
  baseSalary,
  allowance = 0,
  regularCredits,
  holidayCredits,
  standardDays = 26,
  sold,
  sellTarget,
  highendSold,
  highendTarget,
  bonusProgram = 0,
  eventBonus = 0,
  salesTiers,
  highendConfig,
}) => {
  const dailyRate = baseSalary / standardDays;
  const regularPay = dailyRate * regularCredits;
  const holidayPay = dailyRate * holidayCredits * 3;
  const workPay = regularPay + holidayPay;
  const salesBonus = calcSalesBonus(sold, sellTarget, salesTiers);
  const highendBonus = calcHighendBonus(highendSold, highendTarget, highendConfig);
  const allowancePay = Number(allowance) || 0; // phụ cấp cố định, cộng nguyên mỗi tháng
  return {
    workPay: Math.round(workPay),
    allowance: allowancePay,
    salesBonus,
    highendBonus,
    bonusProgram,
    eventBonus,
    total: Math.round(workPay + allowancePay + salesBonus + highendBonus + bonusProgram + eventBonus),
  };
};

// ── LOẠI NGÀY NGHỈ ─────────────────────────────────────────
// Ngày nghỉ do admin đánh dấu (không có check-in/out) nhưng vẫn được hưởng công.
// MANUAL_WORK: admin xác nhận đủ công khi app lỗi / promoter báo qua kênh chat (không có ảnh)
export const DAY_TYPES = { PAID_LEAVE: "paid_leave", ANNUAL_LEAVE: "annual_leave", MANUAL_WORK: "manual_work", UNPAID_LEAVE: "unpaid_leave" };

// Ngày được tính công: có check-out, hoặc là ngày nghỉ được hưởng lương.
// Dùng chung cho bảng lương, dashboard và bảng chấm công để tránh lệch số liệu.
export const isCreditedDay = (r) =>
  !!r && r.leaveStatus !== "pending" && r.dayType !== DAY_TYPES.UNPAID_LEAVE
  && (!!r.checkoutTime || r.dayType === DAY_TYPES.PAID_LEAVE
    || r.dayType === DAY_TYPES.ANNUAL_LEAVE || r.dayType === DAY_TYPES.MANUAL_WORK);

// ── THUẾ TNCN & BẢO HIỂM NGƯỜI LAO ĐỘNG ────────────────────
// Căn cứ: Luật TNCN 2025 (109/2025/QH15, hiệu lực 01/7/2026) — biểu lũy tiến 5 bậc;
//         Nghị quyết 110/2025/UBTVQH15 — giảm trừ gia cảnh 15,5tr / 6,2tr;
//         Nghị định 253/2026/NĐ-CP — tiền ăn ca miễn thuế tối đa 1,2tr/tháng.
// Mọi con số đều sửa được ở màn hình cấu hình (taxConfig/main).
export const DEFAULT_TAX_CONFIG = {
  personalDeduction: 15500000,   // giảm trừ bản thân / tháng
  dependentDeduction: 6200000,   // giảm trừ mỗi người phụ thuộc / tháng
  mealAllowanceCap: 1200000,     // phụ cấp ăn ca được miễn thuế tối đa / tháng
  empSiPct: 8,                   // BHXH người lao động đóng
  empHiPct: 1.5,                 // BHYT
  empUiPct: 1,                   // BH thất nghiệp
  probationMonths: 2,            // thử việc: không tham gia BH, không giảm trừ gia cảnh
  probationTaxMode: "flat",      // "flat": khấu trừ cố định % trên tổng thu nhập | "progressive": lũy tiến, không giảm trừ
  probationFlatRate: 10,         // % khấu trừ khi thử việc (hợp đồng dưới 3 tháng)
  probationFlatMin: 2000000,     // thu nhập dưới mức này không khấu trừ
  brackets: [                    // thu nhập TÍNH THUẾ / tháng; upTo = null là bậc cuối
    { upTo: 10000000, rate: 5 },
    { upTo: 30000000, rate: 10 },
    { upTo: 60000000, rate: 20 },
    { upTo: 100000000, rate: 30 },
    { upTo: null, rate: 35 },
  ],
};

// Ngày kết thúc thử việc = startDate + probationMonths tháng
export const probationEndDate = (startDate, months = DEFAULT_TAX_CONFIG.probationMonths) => {
  if (!startDate) return null;
  const d = new Date(startDate);
  if (isNaN(d)) return null;
  const end = new Date(d);
  end.setMonth(end.getMonth() + months);
  return end;
};

// Tháng được coi là thử việc khi CẢ tháng nằm trong thời gian thử việc.
// Thử việc kết thúc giữa tháng → tháng đó tính là chính thức (admin ghi đè được nếu cần).
// Không có startDate (promoter cũ) → coi như đã qua thử việc, giữ nguyên cách tính hiện hành.
// ── LƯƠNG CƠ BẢN THEO THÁNG ─────────────────────────────────
// users/{uid}.salaryHistory = [{ effectiveFrom: "yyyy-MM", baseSalary, allowance }]
// Đổi lương chỉ có hiệu lực từ tháng chọn trở đi — các tháng trước vẫn tính theo mức cũ.
// Chưa có lịch sử → dùng baseSalary/allowance hiện tại cho mọi tháng (như trước đây).
export const salaryForMonth = (p, month) => {
  const hist = Array.isArray(p?.salaryHistory) ? [...p.salaryHistory]
    .filter(h => h?.effectiveFrom).sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom)) : [];
  if (!hist.length || !month) return { baseSalary: Number(p?.baseSalary) || 0, allowance: Number(p?.allowance) || 0 };
  const hit = hist.filter(h => h.effectiveFrom <= month).pop() || hist[0];
  return { baseSalary: Number(hit.baseSalary) || 0, allowance: Number(hit.allowance) || 0 };
};
// Promoter với lương của đúng tháng đó — đưa vào computeMonthlyPay / calcPayrollDeductions
export const promoterForMonth = (p, month) => ({ ...p, ...salaryForMonth(p, month) });

// Thêm / thay mức lương có hiệu lực từ một tháng. Lần đầu: mức hiện tại được giữ làm mức "từ trước tới nay".
export const upsertSalaryHistory = (p, { effectiveFrom, baseSalary, allowance }) => {
  const base = Array.isArray(p?.salaryHistory) && p.salaryHistory.length
    ? p.salaryHistory
    : [{ effectiveFrom: "2000-01", baseSalary: Number(p?.baseSalary) || 0, allowance: Number(p?.allowance) || 0 }];
  return [...base.filter(h => h.effectiveFrom !== effectiveFrom),
    { effectiveFrom, baseSalary: Number(baseSalary) || 0, allowance: Number(allowance) || 0 }]
    .sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
};

export const isProbationMonth = (startDate, month, months = DEFAULT_TAX_CONFIG.probationMonths) => {
  const end = probationEndDate(startDate, months);
  if (!end || !month) return false;
  const [y, m] = month.split("-").map(Number);
  const lastDayOfMonth = new Date(y, m, 0); // ngày 0 của tháng sau = ngày cuối tháng này
  return end > lastDayOfMonth;
};

// Thuế lũy tiến TỪNG PHẦN: mỗi bậc chỉ áp thuế suất lên phần thu nhập nằm trong bậc đó.
export const calcPIT = (taxableIncome, brackets = DEFAULT_TAX_CONFIG.brackets) => {
  const inc = Number(taxableIncome) || 0;
  if (inc <= 0) return 0;
  const list = (Array.isArray(brackets) && brackets.length ? brackets : DEFAULT_TAX_CONFIG.brackets)
    .slice()
    .sort((a, b) => (a.upTo ?? Infinity) - (b.upTo ?? Infinity));
  let tax = 0, lower = 0;
  for (const b of list) {
    const upper = b.upTo == null ? Infinity : Number(b.upTo);
    if (inc <= lower) break;
    const portion = Math.min(inc, upper) - lower;
    if (portion > 0) tax += portion * (Number(b.rate) || 0) / 100;
    lower = upper;
    if (!isFinite(upper)) break;
  }
  return Math.round(tax);
};

/**
 * Bảo hiểm người lao động đóng + thuế TNCN của một tháng.
 * allowance được coi là phụ cấp ăn ca/hỗ trợ: KHÔNG tính vào căn cứ đóng BH,
 * và được miễn thuế tới mealAllowanceCap.
 */
export const calcPayrollDeductions = ({
  totalIncome = 0, baseSalary = 0, allowance = 0, dependents = 0, nonTaxable = 0,
  startDate = null, month = "", cfg = DEFAULT_TAX_CONFIG, insuranceEnabled = true,
}) => {
  const c = { ...DEFAULT_TAX_CONFIG, ...(cfg || {}) };
  const probation = isProbationMonth(startDate, month, c.probationMonths);
  const insured = insuranceEnabled && !probation;

  const insBase = insured ? Number(baseSalary) || 0 : 0;
  const empSI = Math.round(insBase * c.empSiPct / 100);
  const empHI = Math.round(insBase * c.empHiPct / 100);
  const empUI = Math.round(insBase * c.empUiPct / 100);
  const empTotal = empSI + empHI + empUI;

  // Thử việc theo kiểu khấu trừ cố định (agency): % × TỔNG thu nhập, tăng ca cũng chịu thuế,
  // không giảm trừ gia cảnh, không bảo hiểm.
  if (probation && c.probationTaxMode === "flat") {
    const total = Number(totalIncome) || 0;
    const rate = Number(c.probationFlatRate) || 0;
    const pit = total >= (Number(c.probationFlatMin) || 0) ? Math.round(total * rate / 100) : 0;
    return {
      probation, flatRate: rate, insBase: 0, empSI: 0, empHI: 0, empUI: 0, empTotal: 0,
      mealExempt: 0, nonTaxable: 0, assessable: total, personalDeduction: 0, dependentDeduction: 0,
      taxable: total, pit, net: Math.round(total - pit),
    };
  }

  const mealExempt = Math.min(Number(allowance) || 0, Number(c.mealAllowanceCap) || 0);
  // OT không tính thuế: vẫn nằm trong Gross nhưng loại khỏi thu nhập chịu thuế
  const nonTax = Number(nonTaxable) || 0;
  const assessable = Math.max(0, (Number(totalIncome) || 0) - mealExempt - nonTax);
  const personal = probation ? 0 : Number(c.personalDeduction) || 0;
  const dependentTotal = probation ? 0 : (Number(c.dependentDeduction) || 0) * (Number(dependents) || 0);
  const taxable = Math.max(0, assessable - empTotal - personal - dependentTotal);
  const pit = calcPIT(taxable, c.brackets);

  return {
    probation, insBase, empSI, empHI, empUI, empTotal,
    mealExempt, nonTaxable: nonTax, assessable, personalDeduction: personal, dependentDeduction: dependentTotal,
    taxable, pit, net: Math.round((Number(totalIncome) || 0) - empTotal - pit),
  };
};

// ── TRẠNG THÁI CHẤM CÔNG (7 trạng thái) ────────────────────
// Loại đơn tăng ca: làm thêm giờ trong ngày đi làm, hoặc đi làm cả ngày nghỉ hằng tuần
export const OT_KINDS = { EXTRA: "extra", WEEKLY_OFF: "weekly_off", HOLIDAY: "holiday" };

// Một nguồn duy nhất cho lịch của promoter, bảng ma trận của admin và file Excel.
// Màu ở đây phải khớp với các class .att-s-* trong index.css.
export const ATT_STATUS = {
  full:         { color: "#D6E58D", cls: "att-s-full" },          // đủ 8 tiếng
  missing:      { color: "#FFB0B5", cls: "att-s-missing" },       // thiếu check-in/out
  short:        { color: "#F9E1AB", cls: "att-s-short" },         // đi làm thiếu giờ
  holiday:      { color: "#DCCCEC", cls: "att-s-holiday" },       // ngày lễ
  annualLeave:  { color: "#BCD8EC", cls: "att-s-annual" },        // nghỉ phép năm
  unpaidLeave:  { color: "#F89C74", cls: "att-s-unpaid" },        // nghỉ không phép
  weeklyOff:    { color: "#C8A98A", cls: "att-s-weekoff" },       // nghỉ tuần
  future:       { color: null,      cls: "att-s-future" },        // ngày chưa tới
};

// Trạng thái nào cho phép promoter gửi giải trình
export const CAN_APPEAL = new Set(["missing", "short"]);

/**
 * Phân loại một ngày → key của ATT_STATUS.
 * Thứ tự ưu tiên: ngày lễ → loại nghỉ đã đăng ký → chấm công thực tế.
 * Ngày lễ được ưu tiên vì hệ số lương ×3 quan trọng hơn số giờ.
 */
export const getAttendanceStatus = ({ rec, dateStr, todayStr, isHoliday = false, appealApproved = false, work = LEGACY_WORK_CONFIG }) => {
  if (work.legacy ? (rec?.isHoliday || (!rec && isHoliday)) : isHoliday) return "holiday";

  if (rec) {
    if (rec.dayType === DAY_TYPES.ANNUAL_LEAVE) return "annualLeave";
    if (rec.dayType === DAY_TYPES.UNPAID_LEAVE) return "unpaidLeave";
    // Nghỉ có lương và ngày admin xác nhận đủ công đều tính là đủ công
    if (rec.dayType === DAY_TYPES.PAID_LEAVE || rec.dayType === DAY_TYPES.MANUAL_WORK) return "full";
    if (appealApproved || rec.appealApproved) return "full";
    if (!rec.checkinTime || !rec.checkoutTime) return "missing";
    return (Number(rec.totalHours) || 0) >= requiredHours(rec, work) ? "full" : "short";
  }

  if (dateStr > todayStr) return "future";
  return "weeklyOff"; // ngày đã qua không có bản ghi = nghỉ tuần
};

// Ngày nghỉ do promoter đăng ký, đang chờ quản lý duyệt
export const isPendingLeave = (rec) => !!rec && rec.leaveStatus === "pending";


// ════════════════════════════════════════════════════════════
// CẤU HÌNH TẬP TRUNG — có phiên bản theo "tháng áp dụng"
// payrollConfig/{yyyy-MM}     → work, bonus, tax, rules   (mọi người đọc)
// payrollCostConfig/{yyyy-MM} → cost                      (chỉ admin đọc)
// Tháng chưa có phiên bản nào áp dụng → dùng quy tắc cũ (legacy) để số lương đã trả không đổi.
// ════════════════════════════════════════════════════════════

export const DEFAULT_WORK_CONFIG = {
  standardDays: 26,        // số ngày công chuẩn / tháng
  standardHours: 7,        // giờ làm chuẩn / ngày — mốc "đủ công" và mốc bắt đầu tính làm thêm giờ
  rateHours: 8,            // số giờ quy đổi đơn giá: đơn giá giờ = lương ÷ số ngày công chuẩn ÷ số này (theo agency)
  halfShiftHours: 3.5,     // giờ của nửa ca (tính 0,5 công)
  shortTolerance: 0,       // được thiếu bao nhiêu giờ vẫn tính đủ công
  otRate: 150,             // % đơn giá giờ cho tăng ca ngày thường (Điều 98 BLLĐ: tối thiểu 150%)
  weeklyOffOtRate: 200,    // % đơn giá giờ khi làm vào ngày nghỉ hằng tuần (Điều 98: tối thiểu 200%)
  holidayPaidRate: 100,    // % lương ngày cho mỗi ngày lễ — đi làm hay không đều được
  holidayOtRate: 300,      // % lương ngày cho phần đi làm ngày lễ, cộng thêm (Điều 98: tối thiểu 300%)
  otTaxExempt: true,       // Luật TNCN 2025: toàn bộ tiền làm thêm giờ miễn thuế từ 01/01/2026
  otRequiresApproval: true,
};

// Cách tính trước khi có cấu hình tập trung — giữ nguyên để các tháng cũ ra đúng số đã trả
export const LEGACY_WORK_CONFIG = {
  standardDays: 26, standardHours: 8, rateHours: 8, halfShiftHours: 4, shortTolerance: 1,
  otRate: 0, weeklyOffOtRate: 0, holidayPaidRate: 0, holidayOtRate: 300,
  otTaxExempt: false, otRequiresApproval: true,
  legacy: true,
};

export const DEFAULT_RULES = {
  maxAppealsPerMonth: 2,   // số giải trình được duyệt tối đa / tháng
  checkoutRemindHour: 22,  // giờ nhắc promoter check-out
  closingDay: 5,           // tháng trước còn sửa được tới hết ngày này
};

const clone = (o) => JSON.parse(JSON.stringify(o));

export const buildDefaultPayrollConfig = () => ({
  work: { ...DEFAULT_WORK_CONFIG },
  bonus: { salesTiers: clone(DEFAULT_SALES_TIERS), highendConfig: { ...DEFAULT_HIGHEND_CONFIG } },
  tax: clone(DEFAULT_TAX_CONFIG),
  cost: clone(DEFAULT_COMPANY_COST),
  rules: { ...DEFAULT_RULES },
});

/**
 * Cấu hình áp dụng cho một tháng.
 * versions: [{ effectiveFrom, work, bonus, tax, rules, cost? }]
 * legacy:   { bonus, tax, cost } — các doc cấu hình cũ (bonusConfig/main, taxConfig/main, companyCostConfig/main)
 */
export const resolvePayrollConfig = (versions = [], legacy = {}, month) => {
  const d = buildDefaultPayrollConfig();
  const v = versions
    .filter(x => x.effectiveFrom && month && x.effectiveFrom <= month)
    .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0];

  if (v) {
    return {
      effectiveFrom: v.effectiveFrom,
      work: { ...d.work, ...(v.work || {}) },
      bonus: {
        salesTiers: v.bonus?.salesTiers?.length ? v.bonus.salesTiers : d.bonus.salesTiers,
        highendConfig: { ...d.bonus.highendConfig, ...(v.bonus?.highendConfig || {}) },
      },
      tax: { ...d.tax, ...(v.tax || {}) },
      cost: { ...d.cost, ...(v.cost || {}) },
      rules: { ...d.rules, ...(v.rules || {}) },
    };
  }
  return {
    effectiveFrom: null,
    work: { ...LEGACY_WORK_CONFIG },
    bonus: {
      salesTiers: legacy.bonus?.salesTiers?.length ? legacy.bonus.salesTiers : d.bonus.salesTiers,
      highendConfig: legacy.bonus?.highendConfig?.unitRate != null ? legacy.bonus.highendConfig : d.bonus.highendConfig,
    },
    tax: { ...d.tax, probationTaxMode: "progressive", ...(legacy.tax || {}) },
    cost: { ...d.cost, ...(legacy.cost || {}) },
    rules: { ...d.rules },
  };
};

// Số giờ phải làm để tính đủ công: nửa ca hay ca đầy đủ, trừ dung sai
export const requiredHours = (rec, work = LEGACY_WORK_CONFIG) => {
  const scheduled = Number(rec?.scheduledHours) || work.standardHours;
  const base = scheduled < work.standardHours ? work.halfShiftHours : work.standardHours;
  return base - (Number(work.shortTolerance) || 0);
};

// Công của một ca: ca đầy đủ = 1, nửa ca = 0,5
export const shiftCredit = (scheduledHours, work = DEFAULT_WORK_CONFIG) =>
  Number(scheduledHours) < work.standardHours ? 0.5 : 1;

/**
 * Tính lương một tháng cho một promoter.
 * NGUỒN DUY NHẤT cho bảng lương admin, supervisor, dashboard promoter và file Excel —
 * trước đây cùng logic này bị chép ở ba nơi.
 */
export const computeMonthlyPay = ({
  promoter, month, cfg,
  attendance = [], sales = [], target = null, programs = [], events = [],
  adjustment = null, holidays = [], overtime = [],
}) => {
  const w = cfg.work;
  const dailyRate = (Number(promoter.baseSalary) || 0) / (Number(w.standardDays) || 26);
  // Đơn giá giờ theo agency: lương ÷ 26 ÷ 8 (tách khỏi ca chuẩn 7 giờ)
  const hourlyRate = dailyRate / (Number(w.rateHours) || Number(w.standardHours) || 8);
  const creditOf = (a) => a.dayCredit ?? 1;

  // Ngày lễ trong tháng — chỉ tính từ ngày vào làm
  const monthHolidays = holidays.filter(h => h.date?.startsWith(month)
    && (!promoter.startDate || h.date >= promoter.startDate));
  const holidaySet = new Set(monthHolidays.map(h => h.date));
  // Ngày lễ: quy tắc cũ chỉ dựa vào cờ isHoliday ghi lúc check-in (đúng như công thức cũ);
  // quy tắc mới lấy danh sách ngày lễ hiện tại làm chuẩn — cờ cũ ghi sai không còn ảnh hưởng.
  const onHoliday = w.legacy ? (a) => !!a.isHoliday : (a) => holidaySet.has(a.date);
  // Nghỉ phép / nghỉ có lương không phải là đi làm: rơi vào ngày lễ thì không được 300%.
  // Theo quy tắc mới ngày lễ đó đã được trả 100% qua paidHolidayDays, đếm nữa là trả 2–3 lần.
  const isLeaveDay = (a) => a.dayType === DAY_TYPES.PAID_LEAVE || a.dayType === DAY_TYPES.ANNUAL_LEAVE;

  // Đơn tăng ca có hiệu lực (đã duyệt, hoặc không bắt buộc duyệt)
  const otReg = {};
  overtime
    .filter(o => o.status === "approved" || (!w.otRequiresApproval && o.status === "pending"))
    .forEach(o => { otReg[o.date] = { hours: Number(o.hours) || 0, kind: o.kind || OT_KINDS.EXTRA, byAdmin: !!o.byAdmin }; });
  // Ngày đi làm đúng vào ngày nghỉ hằng tuần (đã đăng ký) — trả theo giờ ở mức tăng ca ngày nghỉ,
  // KHÔNG tính thêm là công thường (lương 26 công không bao gồm ngày nghỉ tuần).
  // Trùng ngày lễ thì theo ngày lễ, không áp mức ngày nghỉ tuần.
  const isWeeklyOffWork = (a) => !w.legacy && w.weeklyOffOtRate > 0 && !onHoliday(a) && !isLeaveDay(a)
    && otReg[a.date]?.kind === OT_KINDS.WEEKLY_OFF;

  const credited = attendance.filter(isCreditedDay);
  const holidayWorked = credited.filter(a => onHoliday(a) && (w.legacy || !isLeaveDay(a)));
  const regularWorked = credited.filter(a => !onHoliday(a) && !isWeeklyOffWork(a));

  const adjDays = Number(adjustment?.extraDays) || 0;
  const regularCredits = regularWorked.reduce((s, a) => s + creditOf(a), 0) + adjDays;
  const holidayCredits = holidayWorked.reduce((s, a) => s + creditOf(a), 0);

  // ── Lương công + ngày lễ ──
  let workPay, holidayPay, holidayOtPay, paidHolidayDays;
  if (w.legacy) {
    // Cách cũ: ngày lễ có đi làm ×3 nằm chung trong "lương công", lễ không đi làm không có lương
    workPay = dailyRate * regularCredits + dailyRate * holidayCredits * (w.holidayOtRate / 100);
    holidayPay = 0;
    holidayOtPay = 0;
    paidHolidayDays = 0;
  } else {
    workPay = dailyRate * regularCredits;
    paidHolidayDays = monthHolidays.length;                                   // đi làm hay không đều được
    holidayPay = dailyRate * (w.holidayPaidRate / 100) * paidHolidayDays;
    holidayOtPay = 0;                                                         // tính theo giờ ở vòng lặp tăng ca
  }

  // ── Làm ngày lễ, tăng ca, làm ngày nghỉ tuần — tất cả tính THEO GIỜ đã đăng ký và được duyệt ──
  // Giờ admin nhập (byAdmin) dùng đúng số nhập. Giờ promoter tự đăng ký thì không vượt giờ làm thực tế.
  //  · Ngày lễ: mọi giờ làm (kể cả quá 8 giờ) × mức ngày lễ; không đăng ký thì không tính.
  //  · Ngày thường: chỉ phần vượt ca chuẩn × mức tăng ca ngày thường.
  //  · Ngày nghỉ tuần: mọi giờ làm × mức ngày nghỉ tuần.
  const recByDate = {};
  credited.forEach(a => { recByDate[a.date] = a; });
  let otHours = 0, otPay = 0, weeklyOffHours = 0, weeklyOffPay = 0;
  let holidayOtHours = 0, holidayWorkDays = 0, weeklyOffDays = 0;
  if (!w.legacy) {
    for (const [date, e] of Object.entries(otReg)) {
      if (!date.startsWith(month) || !(e.hours > 0)) continue;
      const rec = recByDate[date];
      if (rec && isLeaveDay(rec)) continue;          // ngày nghỉ phép thì không có tăng ca
      const actual = Number(rec?.totalHours) || 0;
      if (holidaySet.has(date)) {
        const h = e.byAdmin ? e.hours : Math.min(e.hours, actual);
        if (h <= 0 || !(w.holidayOtRate > 0)) continue;
        holidayOtHours += h; holidayWorkDays += 1;
        holidayOtPay += hourlyRate * (w.holidayOtRate / 100) * h;
      } else if (e.kind === OT_KINDS.WEEKLY_OFF) {
        const h = e.byAdmin ? e.hours : Math.min(e.hours, actual);
        if (h <= 0 || !(w.weeklyOffOtRate > 0)) continue;
        weeklyOffHours += h; weeklyOffDays += 1;
        weeklyOffPay += hourlyRate * (w.weeklyOffOtRate / 100) * h;
      } else if (e.kind !== OT_KINDS.HOLIDAY) {   // đơn "ngày lễ" mà ngày đó không còn là lễ thì bỏ qua
        const h = e.byAdmin ? e.hours : Math.min(e.hours, Math.max(0, actual - w.standardHours));
        if (h <= 0 || !(w.otRate > 0)) continue;
        otHours += h;
        otPay += hourlyRate * (w.otRate / 100) * h;
      }
    }
  }

  // ── Thưởng ──
  const sold = sales.length;
  const highendSold = sales.filter(x => x.isHighend).length;
  const sellTarget = Number(target?.sellTarget) || 0;
  const highendTarget = Number(target?.highendTarget) || 0;
  const salesBonus = calcSalesBonus(sold, sellTarget, cfg.bonus.salesTiers);
  const highendBonus = calcHighendBonus(highendSold, highendTarget, cfg.bonus.highendConfig);

  let bonusProgram = 0;
  programs.forEach(prog => {
    if (prog.status === "revoked") return;   // đã thu hồi → không tính thưởng
    sales.forEach(x => {
      if (prog.skus?.includes(x.model) && x.saleDate >= prog.startDate && x.saleDate <= prog.endDate) {
        bonusProgram += Number(prog.amountPerUnit) || 0;
      }
    });
  });
  const adjBonus = Number(adjustment?.extraBonus) || 0;
  bonusProgram += adjBonus;
  const eventBonus = events.reduce((s, b) => s + (Number(b.amount) || 0), 0);
  const allowance = Number(promoter.allowance) || 0;
  const otManual = Number(adjustment?.extraOtNonTax) || 0;

  const total = Math.round(
    workPay + holidayPay + holidayOtPay + otPay + weeklyOffPay + allowance
    + salesBonus + highendBonus + bonusProgram + eventBonus + otManual
  );
  // Khoản không chịu thuế: OT nhập tay + (theo luật mới) toàn bộ tiền tăng ca và làm ngày lễ
  const nonTaxable = Math.round(otManual + (w.otTaxExempt ? holidayOtPay + otPay + weeklyOffPay : 0));

  return {
    dailyRate, hourlyRate,
    regularCredits, holidayCredits: w.legacy ? holidayCredits : holidayWorkDays, holidayOtHours,
    paidHolidayDays, adjDays, adjBonus,
    dayCreditTotal: regularCredits + (w.legacy ? holidayCredits : paidHolidayDays),
    // "Lương công" gồm cả lương ngày lễ 100% — để số công và lương công khớp nhau khi nhìn
    // (26 công ↔ đủ lương cơ bản). holidayPay giữ lại chỉ để hiện "trong đó", không cộng thêm.
    workPay: Math.round(workPay + holidayPay),
    holidayPay: Math.round(holidayPay),
    holidayOtPay: Math.round(holidayOtPay),
    otHours, otPay: Math.round(otPay),
    weeklyOffHours, weeklyOffPay: Math.round(weeklyOffPay), weeklyOffDays,
    allowance, salesBonus, highendBonus, bonusProgram, eventBonus,
    otNonTax: otManual,
    sold, highendSold, sellTarget, highendTarget,
    total, nonTaxable,
  };
};

// Số công của tháng — cùng cách đếm với bảng lương (computeMonthlyPay.dayCreditTotal),
// chưa gồm phần điều chỉnh công tay (chỉ có ở bảng lương).
// Quy tắc mới: mỗi ngày lễ (từ ngày vào làm) được 1 công dù có đi làm hay không.
export const countPaidDays = ({ records = [], holidays = [], month, startDate = null, work = LEGACY_WORK_CONFIG, overtime = [] }) => {
  const creditOf = (r) => r.dayCredit ?? 1;
  const woDates = new Set(work.legacy || !(work.weeklyOffOtRate > 0) ? [] : overtime
    .filter(o => o.kind === OT_KINDS.WEEKLY_OFF && (o.status === "approved" || (!work.otRequiresApproval && o.status === "pending")))
    .map(o => o.date));
  const credited = records.filter(r => isCreditedDay(r) && !woDates.has(r.date));
  if (work.legacy) return credited.reduce((a, r) => a + creditOf(r), 0);
  const hol = holidays.filter(h => h.date?.startsWith(month) && (!startDate || h.date >= startDate));
  const holSet = new Set(hol.map(h => h.date));
  return credited.filter(r => !holSet.has(r.date)).reduce((a, r) => a + creditOf(r), 0) + hol.length;
};
